"use client";
import {
  AlertTriangle,
  ChevronRight,
  Download,
  FileArchive,
  FolderOpen,
  LoaderCircle,
  Search,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  formatBytes,
  truncateMiddle,
} from "../../shared/format";

const DEFAULT_DISCORD_URL = "https://discord.gg/vMHHNShhBK";


interface CommunityFile {
  id: string;
  name: string;
  version: string;
  owner: string;
  size: number;
  suspicion: number;
  verdict: string;
  sha256: string;
  url: string;
  downloadUrl?: string;
  discordUrl?: string;
  // True when the catalog serves the file itself rather than linking out to a
  // host whose URL can expire.
  hosted?: boolean;
  fileName?: string;
  notes?: string;
  addedAt: string;
}


function communityDownloadName(file: CommunityFile) {
  if (file.fileName) return file.fileName;
  const slug = `${file.name}-${file.version}`
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
  return `${slug || file.id}.jar`;
}


interface CommunityFolder {
  id: string;
  name: string;
  description: string;
  owner: string;
  updatedAt: string;
  files: CommunityFile[];
}


interface CommunityFolderSummary {
  id: string;
  name: string;
  description: string;
  owner: string;
  manifest: string;
  fileCount: number;
  updatedAt: string;
  discordUrl?: string;
}


interface CommunityCatalog {
  schemaVersion: number;
  updatedAt: string;
  folders: CommunityFolderSummary[];
}


export function CommunityDownloadsView() {
  const [catalog, setCatalog] = useState<CommunityCatalog | null>(null);
  const [activeFolder, setActiveFolder] = useState<CommunityFolder | null>(null);
  const [pendingDownload, setPendingDownload] =
    useState<CommunityFile | null>(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [catalogError, setCatalogError] = useState("");
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let active = true;
    void fetch("/community/index.json", { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) throw new Error("Community catalog is unavailable.");
        return (await response.json()) as CommunityCatalog;
      })
      .then((value) => {
        if (active) setCatalog(value);
      })
      .catch((error) => {
        if (active) {
          setCatalogError(
            error instanceof Error
              ? error.message
              : "Community catalog is unavailable.",
          );
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const openFolder = useCallback(async (folder: CommunityFolderSummary) => {
    setCatalogError("");
    setFolderLoading(true);
    try {
      const response = await fetch(`/community/${folder.manifest}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error(`${folder.name} could not be loaded.`);
      }
      const value = (await response.json()) as CommunityFolder;
      setActiveFolder(value);
      setQuery("");
    } catch (error) {
      setCatalogError(
        error instanceof Error
          ? error.message
          : "Community folder is unavailable.",
      );
    } finally {
      setFolderLoading(false);
    }
  }, []);

  const matchingFiles = (activeFolder?.files ?? []).filter((file) =>
    `${file.name} ${file.version} ${file.owner} ${file.sha256} ${file.notes ?? ""}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  const totalFiles =
    catalog?.folders.reduce((total, folder) => total + folder.fileCount, 0) ?? 0;
  const activeFolderSummary = catalog?.folders.find(
    (folder) => activeFolder && folder.id === activeFolder.id,
  );

  const confirmDownload = useCallback(() => {
    if (!pendingDownload) return;
    const downloadUrl = pendingDownload.downloadUrl ?? pendingDownload.url;
    // prettier-ignore
    const discordUrl = pendingDownload.discordUrl ?? activeFolderSummary?.discordUrl ?? DEFAULT_DISCORD_URL;

    if (pendingDownload.hosted) {
      // The catalog serves this file itself, so a same-origin anchor starts the
      // transfer straight away with a readable name and no popup to block.
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = communityDownloadName(pendingDownload);
      anchor.rel = "noopener";
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } else {
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    }
    window.location.assign(discordUrl);
    setPendingDownload(null);
  }, [pendingDownload, activeFolderSummary]);

  // The custom modal traps focus nowhere and is not <dialog>, so closing it
  // with Escape (and returning focus to the page) needs an explicit handler.
  useEffect(() => {
    if (!pendingDownload) return;
    confirmButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPendingDownload(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pendingDownload]);

  return (
    <div className="community-workspace">
      <section className="community-hero">
          <div>
          <div className="section-kicker">COMMUNITY RELEASE CATALOG</div>
          <h1>{activeFolder ? activeFolder.name : "Community downloads"}</h1>
          <p>
            {activeFolder
              ? activeFolder.description
              : "Browse split, cache-friendly maintainer folders. Every release is cataloged with a matching JarLab report and SHA-256, including clearly labelled high-risk research files."}
          </p>
          {catalog && !activeFolder && (
            <small className="community-catalog-summary">
              {catalog.folders.length} folders · {totalFiles} verified files ·
              schema v{catalog.schemaVersion}
            </small>
          )}
        </div>
        <div className="community-legend" aria-label="Suspicion legend">
          <span className="is-safe">
            <ShieldCheck size={14} />
            0–5 clear
          </span>
          <span className="is-review">
            <AlertTriangle size={14} />
            6–29 review
          </span>
          <span className="is-danger">
            <ShieldAlert size={14} />
            30–84 high risk
          </span>
          <span className="is-malicious">
            <ShieldOff size={14} />
            85–100 malicious
          </span>
        </div>
      </section>

      <div className="community-toolbar">
        <div className="community-breadcrumbs">
          <button
            type="button"
            onClick={() => {
              setActiveFolder(null);
              setQuery("");
              setCatalogError("");
            }}
          >
            Community
          </button>
          {activeFolder && (
            <>
              <ChevronRight size={13} />
              <span>{activeFolder.name}</span>
            </>
          )}
        </div>
        {activeFolder && (
          <label className="search-field">
            <Search size={15} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search this folder"
              aria-label="Search community files"
            />
          </label>
        )}
      </div>

      {catalogError ? (
        <div className="panel entry-tool-error">
          <AlertTriangle size={17} />
          {catalogError}
          {activeFolder && (
            <button type="button" onClick={() => setCatalogError("")}>
              Dismiss
            </button>
          )}
        </div>
      ) : !catalog || folderLoading ? (
        <div className="community-loading">
          <LoaderCircle size={20} />
          {folderLoading ? "Loading folder manifest…" : "Loading reviewed catalog…"}
        </div>
      ) : activeFolder ? (
        <section className="community-file-panel">
          <div className="community-table-head">
            <span>Release</span>
            <span>Owner</span>
            <span>Size</span>
            <span>Suspicion</span>
            <span>Action</span>
          </div>
          {matchingFiles.map((file) => (
            <article className="community-file-row" key={file.id}>
              <div>
                <FileArchive size={18} />
                <span>
                  <strong>{file.name}</strong>
                  <small>
                    v{file.version} · {truncateMiddle(file.sha256, 12, 10)}
                    {file.hosted === false && " · external link"}
                  </small>
                </span>
              </div>
              <span>{file.owner}</span>
              <span>{formatBytes(file.size)}</span>
              <span
                className={`community-risk ${
                  file.suspicion <= 5
                    ? "is-safe"
                    : file.suspicion < 30
                      ? "is-review"
                      : file.suspicion < 85
                        ? "is-danger"
                        : "is-malicious"
                }`}
                title={`${file.verdict} · ${file.suspicion}/100`}
              >
                {file.suspicion <= 5 ? (
                  <ShieldCheck size={14} />
                ) : (
                  <AlertTriangle size={14} />
                )}
                {file.suspicion}/100
              </span>
              <div className="community-file-actions">
                <button
                  type="button"
                  onClick={() => setPendingDownload(file)}
                  aria-label={`Download ${file.name} and open its Discord`}
                >
                  <Download size={14} />
                  Download
                </button>
              </div>
            </article>
          ))}
          {!matchingFiles.length && (
            <div className="empty-panel">
              <FolderOpen size={26} />
              <strong>No verified files in this folder yet</strong>
              <span>
                Use scripts/community_add.py locally with a matching JarLab
                JSON report to add one.
              </span>
            </div>
          )}
        </section>
      ) : (
        <section className="community-folder-grid">
          {catalog.folders.map((folder) => (
            <button
              type="button"
              key={folder.id}
              onClick={() => void openFolder(folder)}
            >
              <span className="community-folder-icon">
                <FolderOpen size={24} />
              </span>
              <span>
                <strong>{folder.name}</strong>
                <small>{folder.description}</small>
              </span>
              <span>
                {folder.fileCount} file{folder.fileCount === 1 ? "" : "s"}
                <ChevronRight size={15} />
              </span>
            </button>
          ))}
        </section>
      )}

      <section className="community-policy">
        <ShieldAlert size={18} />
        <div>
          <strong>Scanner results are evidence, not certainty</strong>
          <p>
            Community entries may include malicious research files. JarLab
            requires a matching report and hash, but static scans can produce
            false positives and false negatives. Re-scan and isolate downloads
            before running them.
          </p>
        </div>
      </section>

      {pendingDownload && (
        <div
          className="community-warning-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setPendingDownload(null);
          }}
        >
          <section
            className={`community-warning-dialog ${
              pendingDownload.suspicion >= 85 ? "is-malicious" : ""
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="community-warning-title"
          >
            <div className="community-warning-icon">
              <ShieldAlert size={24} />
            </div>
            <div>
              <div className="section-kicker">DOWNLOAD SAFETY CHECK</div>
              <h2 id="community-warning-title">
                This file may be dangerous
              </h2>
              <p>
                JarLab reported <strong>{pendingDownload.verdict}</strong> at{" "}
                <strong>{pendingDownload.suspicion}/100</strong>. Community
                files are supplied by third parties and may intentionally
                contain malware for research.
              </p>
              <p>
                Static scans are not definitive and can produce false positives
                or false negatives. Only continue if you trust the source and
                can inspect the file in an isolated environment.
              </p>
              <dl>
                <div>
                  <dt>File</dt>
                  <dd>
                    {pendingDownload.name} v{pendingDownload.version}
                  </dd>
                </div>
                <div>
                  <dt>SHA-256</dt>
                  <dd>{pendingDownload.sha256}</dd>
                </div>
              </dl>
              <div className="community-warning-actions">
                <button
                  type="button"
                  onClick={() => setPendingDownload(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  ref={confirmButtonRef}
                  onClick={confirmDownload}
                >
                  I understand, download
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
